import { type Column, type ColumnDef } from "@tanstack/react-table";
import { type TFunction } from "i18next";

import { TRADER_TYPE_LABEL_KEYS } from "@/containers/TraderDetails/components/Form/Components/TraderDetailsStepOne/utils";

import { SEARCHABLE_TRADER_FIELDS } from "./constants";
import { type TraderColumnMeta, type TraderListItem } from "./types";

export const getColumnMeta = (columnDef: ColumnDef<TraderListItem, unknown>) =>
  columnDef.meta as TraderColumnMeta | undefined;

export const getColumnSortAria = (column: Column<TraderListItem, unknown>) => {
  const sortDirection = column.getIsSorted();

  if (sortDirection === "asc") {
    return "ascending";
  }

  if (sortDirection === "desc") {
    return "descending";
  }

  return undefined;
};

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

export const getTraderId = (trader: TraderListItem) =>
  trader.id === undefined || trader.id === null ? undefined : trader.id;

export const getTraderName = (trader: TraderListItem) => trader.name?.trim() || "";

export const getTraderArea = (trader: TraderListItem) =>
  trader.location_name?.trim() || trader.location_details?.name?.trim() || "";

export const getFiniteNumber = (value: number | null | undefined) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return undefined;
};

export const getTraderTypeLabel = (trader: TraderListItem, t: TFunction) => {
  const traderType = trader.trader_type?.trim();

  if (!traderType) {
    return "";
  }

  const labelKey = TRADER_TYPE_LABEL_KEYS[traderType];
  return labelKey ? t(labelKey) : traderType;
};

const getTimestamp = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const getLastActiveTimestamp = (trader: TraderListItem) => getTimestamp(trader.last_active);

export const getRegisteredOnTimestamp = (trader: TraderListItem) =>
  getTimestamp(trader.registered_on);

// Older backends only send the `exchange_done_count` alias.
export const getDeliveriesCompletedCount = (trader: TraderListItem) =>
  trader.deliveries_completed_count ?? trader.exchange_done_count;

export const filterTraders = (traders: TraderListItem[], searchTerm?: string) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  if (!query) {
    return traders;
  }

  return traders.filter(trader =>
    SEARCHABLE_TRADER_FIELDS.some(field =>
      normalizeSearchValue(trader[field] ?? "").includes(query)
    )
  );
};

export const getTraderRowId = (trader: TraderListItem, index: number) => {
  const traderId = getTraderId(trader);

  if (traderId !== undefined) {
    return String(traderId);
  }

  return `${index}-${getTraderName(trader)}-${getTraderArea(trader)}`;
};
